import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1709548145082-04d0cde481d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const navigate = useNavigate();
  const { applyAuth } = useAuth();
  const [form, setForm] = useState({ name: "", restaurant_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      applyAuth(data);
      toast.success("Account creato! Benvenuto su MenuOne.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white text-gray-900">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-sm mo-fade-up">
          <div className="lg:hidden mb-8"><Link to="/"><Logo /></Link></div>
          <h1 className="font-display font-bold text-4xl mb-2">Crea il tuo account</h1>
          <p className="text-gray-500 mb-4">Inizia a costruire il tuo menù digitale.</p>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#5aa469]/10 text-[#4a8a56] text-sm font-medium px-3 py-1.5 mb-8" data-testid="reg-trial-badge">
            <span className="h-1.5 w-1.5 rounded-full bg-[#5aa469]" /> Prova gratis per 14 giorni sui piani mensili, poi piano Free o a pagamento
          </div>

          <Button onClick={googleLogin} variant="outline" className="w-full h-11 rounded-full border-gray-300 bg-white hover:bg-gray-50 mb-5" data-testid="google-register-btn">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-5 w-5 mr-2" /> Continua con Google
          </Button>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1 bg-gray-200" /><span className="text-xs text-gray-400 uppercase tracking-wider">oppure</span><div className="h-px flex-1 bg-gray-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Il tuo nome</Label>
              <Input id="name" required value={form.name} onChange={set("name")} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" placeholder="Mario Rossi" data-testid="reg-name" />
            </div>
            <div>
              <Label htmlFor="rest">Nome attività</Label>
              <Input id="rest" value={form.restaurant_name} onChange={set("restaurant_name")} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" placeholder="Trattoria da Mario" data-testid="reg-restaurant" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" placeholder="tua@email.it" data-testid="reg-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" placeholder="Almeno 6 caratteri" data-testid="reg-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold active:scale-95 transition-transform" data-testid="reg-submit">
              {loading ? "Creazione…" : "Registrati"}
            </Button>
          </form>
          <p className="text-sm text-gray-500 mt-6 text-center">
            Hai già un account? <Link to="/login" className="text-[#5aa469] font-medium hover:underline" data-testid="to-login">Accedi</Link>
          </p>
        </div>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex justify-end"><Link to="/"><Logo onDark /></Link></div>
          <div>
            <h2 className="font-display font-bold text-4xl leading-tight mb-3 text-white">Il tuo menù, digitale in minuti</h2>
            <p className="text-white/80 max-w-sm">Categorie illimitate, foto dei piatti, QR code pronto da stampare.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
